/**
 * Proposta — EDITOR por versão (DEMO-03, telas/Proposta.tsx), persistente.
 * Rascunho: campos interativos, "Salvar versão", "Enviar ao Cliente".
 * Versão emitida: somente leitura (histórico); alteração = "Gerar nova versão".
 * O servidor confere tudo de novo (saneamento, pendências, permissões) e
 * calcula os totais; a tela usa o mesmo módulo de regras.
 */
import { CadeiaDaOportunidade } from '../Pedidos.tsx';
import { Fragment, useState } from 'react';
import { api } from '../../api.ts';
import { navegar } from '../../rotas.ts';
import { useSessao } from '../../sessao.tsx';
import { Ajuda, Estado, Modal, Nota, ResumoIndicadores, SeloSituacao } from '../ui.tsx';
import { SeloTipo } from '../filtros.tsx';
import { CampoData } from '../CampoData.tsx';
import { CampoCep, CampoCnpj, SeletorUf } from '../campos.tsx';
import { useAmbiente } from '../../ambiente.tsx';
import { ModalRegistrarAtividade } from '../Atividades.tsx';
import { TIPOS, dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import {
  ORIGENS_PTAX,
  PAGADORES,
  SITUACOES_VERSAO,
  arred,
  custoCotacaoReais,
  usaPtaxDaProposta,
  temItemEmDolar,
  ptaxDaProposta,
  custoItemReais,
  economia,
  economiaLinha,
  economiaVersao,
  entidadeDe,
  enderecoTexto,
  RECEBIMENTO_TRADEIN,
  recebimentoTradein,
  MARCOS_RECEBIMENTO,
  type MarcoRecebimento,
  liquidaEstimada,
  abatimentoEfetivo,
  linhasResultadoDaVersao,
  locaisDaVersao,
  MARGEM_DESEJADA_PADRAO,
  margemPct,
  normalizarFrete,
  novaPtax,
  parcelasDaCondicao,
  parceiroSelecionado,
  pendenciasEnvio,
  podeEnviar,
  produtoDoItem,
  ptaxUtilizada,
  rascunhoDe,
  registrarCotacao,
  restaurarPtaxOficial,
  RESULTADO_LINHA,
  sincronizarCotacoes,
  substituirPtax,
  tipoDaVersao,
  validarPtaxManual,
  vendaPelaMargem,
  versaoVigente,
  type Cotacao,
  type Endereco,
  type EntidadeClienteRef,
  type ItemProposta,
  type OrigemPtaxManual,
  type PagadorFrete,
  type ProdutoRef,
  type Proposta as TProposta,
  type PtaxCotacao,
  type PtaxManual,
  type PtaxOficial,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { PtaxDaProposta } from './PtaxProposta.tsx';
import { useProposta, agoraSaoPaulo, type DadosProposta } from './contexto.tsx';
import { ProdutoNaLinha } from './ProdutoNaLinha.tsx';
import { RegistrarResultadoProposta } from './NaOportunidade.tsx';
import { EditorLocacao } from './PropostaLocacao.tsx';
import { EditorTradein } from './PropostaTradein.tsx';
import { moeda2, pct, pctNum, ptax4 } from './formatos.ts';
import { BotaoExcluir, depoisDeExcluir } from '../Lixeira.tsx';

type RespostaPtax = { disponivel: true; oficial: PtaxOficial; aviso?: string } | { disponivel: false; motivo: string };

let seq = 0;
const novoId = (prefixo: string) => `${prefixo}${Date.now() % 100000}-${++seq}`;

/** Editor da versão: `numero` ausente = versão vigente. */
export function PropostaVersao({ numero }: { numero?: number }) {
  const { dados } = useProposta();
  const p = dados.proposta;
  if (!p) return <p className="vazio">Proposta não encontrada.</p>;
  const salvo = numero ? p.versoes.find((x) => x.numero === numero) : versaoVigente(p);
  if (!salvo) return <p className="vazio">Versão não encontrada.</p>;
  // Reinicia o rascunho local sempre que a versão gravada muda (após salvar, enviar, regra de comissão…).
  return <Editor key={`${salvo.numero}-${p.atualizado_em ?? ''}-${salvo.situacao}`} p={p} salvo={salvo} />;
}

function Editor({ p, salvo }: { p: TProposta & { atualizado_em: string | null }; salvo: Versao }) {
  const { d, dados, aplicar, nomeUsuario, eu } = useProposta();
  const { pode } = useSessao();
  const o = dados.oportunidade;
  const hoje = dados.opcoes.hoje;
  const [rasc, setRasc] = useState<Versao>(() => structuredClone(salvo));
  const [resultado, setResultado] = useState(false);
  const recarregarProposta = async () => {
    setResultado(false);
    aplicar(await api.get<DadosProposta>(`/api/oportunidades/${o.id}/proposta`));
  };
  const [atividade, setAtividade] = useState(false);
  // P2-30: consultas (versões, cotações e PTAX) num só painel, aberto abaixo das Cotações
  const [painel, setPainel] = useState<Consulta | null>(null);
  // D11: itens com o detalhe aberto (Fabricante, Unidade, Parceiro, Disponibilidade, Observação)
  const [detalhes, setDetalhes] = useState<string[]>([]);
  const alternarDetalhe = (id: string) => setDetalhes((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const cliente = d.clientes[0];
  const aberta = !['ganha', 'ganha_parcialmente', 'perdida', 'cancelada'].includes(o.estagio);
  const editavel = salvo.situacao === 'rascunho' && aberta && pode('editar', 'proposta');
  const tipo = tipoDaVersao(salvo);
  const v = editavel ? rasc : salvo;
  const alterado = editavel && JSON.stringify(rasc) !== JSON.stringify(salvo);
  const vigente = versaoVigente(p);
  const eVigente = vigente.numero === v.numero;
  const base = `/oportunidades/${o.id}/propostas/${p.id}`;
  const custoR$ = (i: ItemProposta) => custoItemReais(i, d, v.ptax);
  const set = (patch: Partial<Versao>) => setRasc({ ...rasc, ...patch });
  const setItem = (id: string, patch: Partial<ItemProposta>, recalcular = true) =>
    setRasc({
      ...rasc,
      itens: rasc.itens.map((i) => {
        if (i.id !== id) return i;
        const n = { ...i, ...patch };
        // Alterar entidade ou custo à mão desfaz o vínculo com a cotação selecionada.
        if (('entidadeId' in patch || 'custoUnitMoeda' in patch) && !('cotacaoId' in patch)) {
          n.cotacaoId = undefined;
          n.ptaxUtilizada = undefined;
        }
        if ('precoUnit' in patch) return { ...n, precoManual: true };
        return recalcular && !n.precoManual ? { ...n, precoUnit: vendaPelaMargem(custoItemReais(n, d, v.ptax), n.margemDesejada) } : n;
      }),
    });
  const linhas = v.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: custoR$(i), vendaUnit: i.precoUnit }));
  const eco = economia(linhas, v.frete);
  // sem o parâmetro de abatimento: "não configurado" (nenhum percentual suposto)
  const abat = abatimentoEfetivo(v.abatimento, dados.opcoes.abatimento);
  const locais = locaisDaVersao(d, v);
  const entidadesCliente = cliente.entidades.filter((e) => e.situacao === 'Ativa');
  const contatos = cliente.contatos.filter((k) => k.situacao === 'Ativo');
  const pendencias = pendenciasEnvio(rasc, d);

  const executar = async (f: () => Promise<DadosProposta | void>) => {
    setErro('');
    setOcupado(true);
    try {
      const r = await f();
      if (r) aplicar(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  const salvar = () => executar(() => api.put<DadosProposta>(`/api/propostas/${p.id}/rascunho`, { versao: rasc, atualizado_em: p.atualizado_em }));
  const enviar = () => executar(() => api.post<DadosProposta>(`/api/propostas/${p.id}/enviar`, { atualizado_em: p.atualizado_em }));
  const gerarNovaVersao = () =>
    executar(async () => {
      const r = await api.post<{ numero: number }>(`/api/propostas/${p.id}/nova-versao`, {});
      navegar(`${base}/v/${r.numero}`);
      return api.get<DadosProposta>(`/api/oportunidades/${o.id}/proposta`);
    });

  /** Item pelo PN/SKU do catálogo (descrição, fabricante, categoria/pilar e tipo vêm do cadastro). */
  const adicionarItem = (pr: ProdutoRef) => {
    const c = v.cotacoes.find((x) => x.produtoId === pr.id);
    const novo: ItemProposta = { id: novoId('i'), produtoId: pr.id, quantidade: 1, entidadeId: c?.entidadeId ?? '', custoUnitMoeda: c?.custoUnitMoeda ?? 0, margemDesejada: MARGEM_DESEJADA_PADRAO, precoUnit: 0, precoManual: false, disponibilidade: 'A confirmar', prazoDias: c?.prazoDias ?? 10, observacao: '', resultado: 'pendente', quantidadeAceita: 0, ...(c ? { cotacaoId: c.id, ...(c.ptax && ptaxUtilizada(c.ptax) ? { ptaxUtilizada: ptaxUtilizada(c.ptax) } : {}) } : {}) };
    const custo = c ? (pr.moeda === 'USD' ? c.custoUnitMoeda * (novo.ptaxUtilizada ?? v.ptax) : c.custoUnitMoeda) : 0;
    novo.precoUnit = vendaPelaMargem(arred(custo), novo.margemDesejada);
    set({ itens: [...rasc.itens, novo] });
  };
  /** Trocar o produto de uma linha: desfaz o vínculo com a cotação do produto anterior. */
  const trocarProduto = (itemId: string, pr: ProdutoRef) => setItem(itemId, { produtoId: pr.id, cotacaoId: undefined, ptaxUtilizada: undefined, custoUnitMoeda: 0 });
  const usarCotacao = (itemId: string, indice: number) => {
    const c = v.cotacoes[indice];
    if (!c) return;
    setRasc(selecionar(rasc, itemId, c.id));
  };
  const selecionar = (x: Versao, itemId: string, cotacaoId: string) => {
    const c = x.cotacoes.find((y) => y.id === cotacaoId)!;
    const n = x.itens.findIndex((i) => i.id === itemId) + 1;
    return sincronizarCotacoes({ ...x, itens: x.itens.map((i) => (i.id === itemId ? { ...i, cotacaoId } : i)), historicoCotacoes: registrarCotacao(x, `Item ${n}: cotação ${entidadeDe(d, c.entidadeId)?.fantasia ?? '—'} selecionada`, eu, agoraSaoPaulo()) }, d);
  };

  const temPtax = tipo === 'venda' && (temItemEmDolar(v, d) || usaPtaxDaProposta(v));
  const recalcular = editavel ? (
    <button type="button" className="botao-secundario pequeno" onClick={() => set({ itens: rasc.itens.map((i) => ({ ...i, precoManual: false, precoUnit: vendaPelaMargem(custoR$(i), i.margemDesejada) })) })} data-teste="recalcular">
      Recalcular pela margem desejada
    </button>
  ) : null;
  return (
    <div className="editor-proposta" data-teste="editor-proposta">
      {/* P2-30: título com o seletor de versões e "Voltar à oportunidade"; as ações ficam no rodapé, depois do preenchimento */}
      <div className="titulo-versao-proposta" data-teste="titulo-proposta">
        <h1 className="titulo-pagina">
          Proposta {p.numero} — versão {v.numero}
        </h1>
        <select value={v.numero} onChange={(e) => navegar(`${base}/v/${e.target.value}`)} aria-label="Versões da proposta" data-teste="seletor-versao">
          {p.versoes
            .slice()
            .reverse()
            .map((x) => (
              <option key={x.numero} value={x.numero}>
                v{x.numero} — {SITUACOES_VERSAO[x.situacao]}
                {x.numero === vigente.numero ? ' (vigente)' : x.situacao === 'rascunho' ? ' (em elaboração)' : ' (histórico)'}
              </option>
            ))}
        </select>
        <a className="botao-link" href={`#/oportunidades/${o.id}`}>
          Voltar à oportunidade
        </a>
      </div>
      <section className="resumo-destaque cabecalho-proposta" data-teste="cabecalho-proposta">
        <div className="cabecalho-registro">
          <div className="selos-cabecalho">
            <SeloTipo tipo={p.tipo} />
            <SeloSituacao texto={SITUACOES_VERSAO[v.situacao]} tom={v.situacao === 'substituida' ? 'encerrado' : v.situacao.startsWith('aceita') ? 'positivo' : 'aberto'} />
            {eVigente ? <span className="selo selo-destaque">vigente</span> : v.situacao === 'rascunho' ? <span className="selo selo-alerta">em elaboração</span> : <span className="selo selo-neutro">histórico</span>}
          </div>
        </div>
        <div className="grade-cabecalho" data-teste="grade-cabecalho-proposta">
          <div>
            <span className="rotulo-campo">Cliente</span>
            <a href={`#/clientes/${cliente.id}`}>{cliente.nome}</a>
          </div>
          <div className="grade-cabecalho-largo">
            <span className="rotulo-campo">Filial/unidade</span>
            <span>
              {(() => {
                const e = cliente.entidades.find((x) => x.id === v.entidadeClienteId);
                return e ? `${e.tipo}: ${e.razao} — ${e.cnpj}` : '—';
              })()}
            </span>
          </div>
          <div>
            <span className="rotulo-campo">Previsão de fechamento</span>
            {dataBr(o.previsaoFechamento)}
          </div>
          <div>
            <span className="rotulo-campo">Responsável</span>
            {nomeUsuario(o.responsavelId)}
          </div>
          <div>
            <span className="rotulo-campo">Datas</span>
            <span>{v.enviadaEm ? `Enviada em ${dataBr(v.enviadaEm)}` : `Criada em ${dataBr(v.data)}`}</span>
            <span className="info-secundaria">Válida até {dataBr(v.validade)}</span>
          </div>
        </div>
      </section>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-proposta">
          {erro}
        </p>
      )}
      {dados.pedido && dados.pedido.versao === v.numero && (
        <>
          <p className="faixa-pedido-gerado" data-teste="pedido-gerado-proposta">
            <strong>Versão aceita · Pedido gerado:</strong> <a href={`#/pedidos/${dados.pedido.numero}`}>{dados.pedido.numero}</a>
          </p>
          <CadeiaDaOportunidade numeroPedido={dados.pedido.numero} atual="proposta" />
        </>
      )}
      {editavel && (pendencias.length ? <Estado tipo="bloqueado" titulo="Para enviar ao Cliente, falta" itens={pendencias} teste="pendencias-envio" /> : <Estado tipo="concluido" titulo={alterado ? 'Tudo preenchido: salve a versão e envie ao Cliente' : 'Pronta para enviar ao Cliente'} teste="pendencias-envio" />)}
      {atividade && <ModalRegistrarAtividade clienteId={o.clienteId} oportunidadeId={o.id} aoFechar={() => setAtividade(false)} aoSalvar={() => setAtividade(false)} />}

      {resultado && <RegistrarResultadoProposta propostaId={p.id} v={v} numeroOp={o.numero} motivos={dados.opcoes.motivos_perda} aoFechar={() => setResultado(false)} aoSalvar={() => void recarregarProposta()} />}

      {editavel && alterado && <p className="aviso aviso-info">Há alterações não salvas nesta versão.</p>}
      {tipo === 'venda' && (
        <section className="resumo-economico cartao-resumo" data-teste="resumo-economico" aria-label="Resumo econômico">
          <h2>
            Resumo econômico <span className="texto-secundario">· versão v{v.numero}</span>
            <Ajuda titulo="Tributos e retenções">Tributos e retenções terão contrato específico (tributos aplicáveis, base de cálculo, variações por Venda, Locação, Trade-in, fornecedor, moeda e faturamento) antes da implementação.</Ajuda>
          </h2>
          <ResumoIndicadores
            itens={[
              { rotulo: 'Total de produtos', valor: moeda2(eco.totalProdutos) },
              { rotulo: 'Custo', valor: moeda2(eco.custo) },
              { rotulo: 'Margem bruta antes do frete', valor: moeda2(eco.bruta), detalhe: pct(eco.percentual) },
              { rotulo: 'Margem após frete', valor: moeda2(eco.aposFrete) },
              {
                rotulo: 'Comissão líquida estimada (interna)',
                valor: abat ? moeda2(liquidaEstimada(eco.aposFrete, abat.percentual)) : 'abatimento não configurado',
                teste: 'liquida-venda',
                ajuda:<Ajuda titulo="Comissão líquida estimada">{abat ? `Abatimento de ${pctNum(abat.percentual)} (parâmetro vigente desde ${dataBr(abat.vigenteDesde)}${v.abatimento ? ', congelado no envio' : ''}).` : `O parâmetro de abatimento (comercial.abatimento_comissao) não está configurado${v.abatimento ? ' no envio desta versão' : ''}: a comissão líquida estimada não é calculada.`}</Ajuda>,
              },
              { rotulo: 'Total final da proposta', valor: moeda2(eco.totalFinal) },
              { rotulo: 'Valor a faturar ao cliente', valor: moeda2(eco.aFaturar) },
            ]}
          />
          {/* P2-26: frete em dois destaques maiores no Resumo */}
          <ResumoIndicadores
            teste="destaques-frete"
            itens={[
              { rotulo: 'Valor do frete', valor: eco.frete.modalidade === 'CIF' ? 'R$ 0,00 (incluso — CIF)' : moeda2(eco.frete.valor), icone: 'caminhao', teste: 'valor-frete-resumo' },
              { rotulo: 'Quem paga o frete', valor: PAGADORES[eco.frete.pagador], icone: 'grupo', teste: 'quem-paga-frete' },
            ]}
          />
        </section>
      )}
      {/* revisão 12/10: na Locação e no Trade-in, a Visão interna ocupa o lugar do Resumo econômico (topo) */}
      {v.locacao && <EditorLocacao parte="resumo" v={v} editavel={editavel} set={set} propostaId={p.id} alterado={alterado} />}
      {v.tradein && <EditorTradein parte="resumo" v={v} editavel={editavel} set={set} propostaId={p.id} alterado={alterado} />}
      {tipo !== 'trade_in' ? (
        /* P2-26: identificação, faturamento e entrega num só grupo, lado a lado (Venda e Locação) */
        <section className="secao-editor grupo-identificacao" data-teste="grupo-identificacao">
          <h2>
            {tipo === 'locacao' ? 'Identificação, faturamento e local de instalação' : 'Identificação, faturamento e entrega'}<Ajuda titulo="Identificação, faturamento e entrega">A filial/unidade da proposta é para quem a cotação é emitida; a de faturamento recebe a nota; a entrega pode ser no mesmo endereço do faturamento, em outra filial/unidade cadastrada ou num endereço específico da operação. Tudo fica gravado na versão e vai para o PDF e para o Pedido.</Ajuda>
          </h2>
          <div className="lado-a-lado">
            <fieldset className="form-grade" disabled={!editavel} data-teste="identificacao">
              <legend>Proposta e faturamento</legend>
              <CampoData rotulo="Validade" valor={v.validade} aoMudar={(x) => set({ validade: x })} min={v.data} teste="validade-proposta" desabilitado={!editavel} obrigatorio />
              <label className="coluna-dupla">
                Filial/unidade da proposta
                <select value={v.entidadeClienteId} onChange={(e) => set({ entidadeClienteId: e.target.value })} data-teste="entidade-cliente-proposta">
                  {cliente.entidades.map((e) => (
                    <option key={e.id} value={e.id} disabled={e.situacao === 'Inativa'}>
                      {e.tipo}: {e.razao} — {e.cnpj}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Contato (A/C)
                <select value={v.contatoId} onChange={(e) => set({ contatoId: e.target.value })} data-teste="contato-proposta">
                  <option value="">— nenhum —</option>
                  {contatos.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Responsável Inttrix
                <select value={v.responsavel} onChange={(e) => set({ responsavel: e.target.value })} data-teste="responsavel-proposta">
                  {dados.opcoes.responsaveis.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nome}
                    </option>
                  ))}
                </select>
              </label>
              <FaturamentoEntrega parte="faturamento" v={v} editavel={editavel} set={set} locais={locais} entidadesCliente={entidadesCliente} />
            </fieldset>
            <FaturamentoEntrega parte="entrega" v={v} editavel={editavel} set={set} locais={locais} entidadesCliente={entidadesCliente} />
          </div>
        </section>
      ) : (
      <fieldset className="form-grade" disabled={!editavel} data-teste="identificacao">
          <legend>Identificação</legend>
          <CampoData rotulo="Validade" valor={v.validade} aoMudar={(x) => set({ validade: x })} min={v.data} teste="validade-proposta" desabilitado={!editavel} obrigatorio />
          <label className="coluna-dupla">
            Filial/unidade da proposta
            <select value={v.entidadeClienteId} onChange={(e) => set({ entidadeClienteId: e.target.value })} data-teste="entidade-cliente-proposta">
              {cliente.entidades.map((e) => (
                <option key={e.id} value={e.id} disabled={e.situacao === 'Inativa'}>
                  {e.tipo}: {e.razao} — {e.cnpj}
                </option>
              ))}
            </select>
          </label>
          <label>
            Contato (A/C)
            <select value={v.contatoId} onChange={(e) => set({ contatoId: e.target.value })} data-teste="contato-proposta">
              <option value="">— nenhum —</option>
              {contatos.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.nome}
                </option>
              ))}
            </select>
          </label>
          <label>
            Responsável Inttrix
            <select value={v.responsavel} onChange={(e) => set({ responsavel: e.target.value })} data-teste="responsavel-proposta">
              {dados.opcoes.responsaveis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      )}
      {tipo !== 'locacao' && (
      <section className="secao-editor" data-teste="condicoes-comerciais-frete" aria-label={tipo === 'venda' ? 'Condições comerciais e frete' : 'Condições comerciais'}>
        <h2>{tipo === 'venda' ? 'Condições comerciais e frete' : 'Condições comerciais'}</h2>
        {/* P2-26: pagamento e frete lado a lado, antes dos Itens */}
        <div className={tipo === 'venda' ? 'lado-a-lado' : undefined}>
      <CondicoesComuns v={v} editavel={editavel} set={set} />
      {tipo === 'venda' && (
        <fieldset className="form-grade" disabled={!editavel} data-teste="condicoes-frete">
          <legend>Frete (Venda)</legend>
          <fieldset className="opcoes grupo-radio">
            <legend>Modalidade do frete</legend>
            {(['CIF', 'FOB'] as const).map((m) => (
              <label key={m} className="opcao">
                <input type="radio" name="modalidade" checked={v.frete.modalidade === m} onChange={() => set({ frete: normalizarFrete({ ...v.frete, modalidade: m, pagador: m === 'CIF' ? 'incluso' : 'inttrix' }) })} data-teste={`frete-${m}`} /> {m}
              </label>
            ))}
          </fieldset>
          <fieldset className="opcoes grupo-radio">
            <legend>Responsável econômico pelo frete</legend>
            {(['incluso', 'inttrix', 'cliente'] as PagadorFrete[]).map((pg) => (
              <label key={pg} className="opcao">
                <input type="radio" name="pagador" checked={v.frete.pagador === pg} disabled={(v.frete.modalidade === 'CIF') !== (pg === 'incluso')} onChange={() => set({ frete: normalizarFrete({ ...v.frete, pagador: pg }) })} data-teste={`pagador-${pg}`} /> {PAGADORES[pg]}
              </label>
            ))}
          </fieldset>
          <label>
            Valor do frete (R$)
            <input type="number" min={0} step="0.01" value={v.frete.valor} disabled={v.frete.modalidade === 'CIF'} onChange={(e) => set({ frete: normalizarFrete({ ...v.frete, valor: Number(e.target.value) }) })} data-teste="valor-frete" />
          </label>
          <label>
            <span>
              Frete cotado com (opcional)<Ajuda titulo="Frete cotado com">Só sugere a alocação do frete entre as Ordens; não reparte automaticamente.</Ajuda>
            </span>
            <select value={v.frete.entidadeId ?? ''} disabled={v.frete.modalidade === 'CIF'} onChange={(e) => set({ frete: normalizarFrete({ ...v.frete, entidadeId: e.target.value || undefined }) })} data-teste="frete-entidade">
              <option value="">— não associado —</option>
              {[...new Set(v.itens.map((i) => i.entidadeId).filter(Boolean))].map((id) => (
                <option key={id} value={id}>
                  {entidadeDe(d, id)?.fantasia} — {entidadeDe(d, id)?.cnpj}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="opcoes grupo-radio" disabled>
            <legend>
              O frete será descontado da comissão da Inttrix?<Ajuda titulo="Desconto do frete">Definido pelo responsável econômico: só o FOB pago pela Inttrix desconta da comissão (sem combinação contraditória).</Ajuda>
            </legend>
            <label className="opcao">
              <input type="radio" name="desconta" checked={v.frete.pagador === 'inttrix'} readOnly /> Sim
            </label>
            <label className="opcao">
              <input type="radio" name="desconta" checked={v.frete.pagador !== 'inttrix'} readOnly /> Não
            </label>
          </fieldset>
        </fieldset>
      )}
        </div>
      </section>
      )}
      {/* P2-27: PTAX compacta numa faixa, com "Recalcular pela margem desejada" */}
      {tipo === 'venda' && <PtaxDaProposta v={v} editavel={editavel} aplicar={(nova) => set(nova)} aoHistorico={() => setPainel('ptax')} extra={recalcular} />}
      {tipo === 'venda' && (
        <>
          <section className="secao-editor" data-teste="itens-editor">
            <div className="barra-ferramentas">
              <h2 className="sem-margem">
                Itens<Ajuda titulo="Como a margem é calculada">Margem bruta = venda − custo; margem % = margem ÷ venda × 100 (base = venda; não é markup). Venda calculada = custo ÷ (1 − margem desejada), com arredondamento em centavos. Se a venda for ajustada à mão, a margem efetiva é recalculada e a diferença para a desejada aparece na linha.</Ajuda>
              </h2>
              <span className="espaco" />
              {!temPtax && recalcular}
            </div>
            <div className="rolagem">
              <table className="tabela tabela-compacta tabela-editor" data-teste="itens-internos">
                <thead>
                  <tr>
                    <th>#</th>
                    {/* P4 bloco 3 (I19): rótulos finais; Prazo por item fora da apresentação (vale o Prazo geral; o dado antigo continua no ▸) */}
                    <th className="col-pn">PN</th>
                    <th className="col-descricao">Descrição</th>
                    <th>Qtd</th>
                    <th>Moeda</th>
                    <th>Custo Unit.</th>
                    <th>Custo Total</th>
                    <th className="col-parceiro">Parceiro</th>
                    <th className="col-margem-desejada">Margem desejada (%)</th>
                    <th>Venda Unit.</th>
                    <th>Venda Total</th>
                    <th>Margem R$</th>
                    <th>Margem %</th>
                    {editavel && <th className="col-acoes">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {v.itens.map((i, n) => {
                    const pr = produtoDoItem(d, i);
                    const cu = custoR$(i);
                    // achado HML (18/10): a conversão USD → R$ sai de baixo do custo (desalinhava a linha) e fica no detalhe do item e na dica do campo
                    const conversao = pr.moeda === 'USD' ? `= ${moeda2(cu)} (${usaPtaxDaProposta(v) ? (ptaxDaProposta(v) ? `PTAX da proposta ${ptax4(ptaxDaProposta(v)!)}` : 'PTAX da proposta a definir') : i.cotacaoId ? (i.ptaxUtilizada ? `PTAX ${ptax4(i.ptaxUtilizada)} da cotação selecionada` : 'PTAX da cotação não informada') : 'sem cotação: selecione uma cotação com PTAX'})` : null;
                    const l = economiaLinha({ quantidade: i.quantidade, custoUnit: cu, vendaUnit: i.precoUnit });
                    const efetiva = margemPct(i.precoUnit, cu);
                    const dif = efetiva - i.margemDesejada;
                    const cots = v.cotacoes.map((c, k) => ({ c, k })).filter(({ c }) => c.produtoId === i.produtoId);
                    const ent = i.entidadeId ? entidadeDe(d, i.entidadeId) : undefined;
                    const aberto = detalhes.includes(i.id);
                    return (
                      <Fragment key={i.id}>
                      <tr data-teste={`linha-${n + 1}`}>
                        <td className="nowrap">
                          <button type="button" className="botao-expandir" aria-expanded={aberto} aria-controls={`detalhe-item-${i.id}`} aria-label={`${aberto ? 'Ocultar' : 'Ver'} detalhes do item ${n + 1}`} title="Fabricante, unidade, parceiro, disponibilidade e observação" onClick={() => alternarDetalhe(i.id)} data-teste={`expandir-item-${n + 1}`}>
                            {aberto ? '▾' : '▸'}
                          </button>{' '}
                          {n + 1}
                        </td>
                        {editavel ? (
                          <ProdutoNaLinha pn={pr.sku} descricao={pr.nome} aoEscolher={(x) => x.id !== i.produtoId && trocarProduto(i.id, x)} teste={`produto-${n + 1}`} rotuloLinha={`item ${n + 1}`} />
                        ) : (
                          <>
                            <td className="celula-pn" title={i.produtoCopia ? 'Cópia histórica do produto gravada no envio (ver detalhe do item)' : undefined}>
                              {pr.sku || '—'}
                            </td>
                            <td className="descricao-produto" title={pr.nome}>
                              {pr.nome}
                            </td>
                          </>
                        )}
                        <td>
                          <input type="number" min={1} value={i.quantidade} disabled={!editavel} onChange={(e) => setItem(i.id, { quantidade: Math.max(1, Math.floor(Number(e.target.value)) || 1) }, false)} aria-label={`Quantidade do item ${n + 1}`} className="entrada-curta" data-teste={`quantidade-${n + 1}`} />
                        </td>
                        <td>{pr.moeda}</td>
                        <td>
                          <input type="number" min={0} step="0.01" value={i.custoUnitMoeda} disabled={!editavel} onChange={(e) => setItem(i.id, { custoUnitMoeda: Number(e.target.value) })} title={conversao ?? undefined} aria-label={`Custo unitário do item ${n + 1} em ${pr.moeda}`} className="entrada-media" data-teste={`custo-${n + 1}`} />
                        </td>
                        <td className="nowrap">{moeda2(l.custoTotal)}</td>
                        <td>
                          <select value={i.entidadeId} disabled={!editavel} onChange={(e) => setItem(i.id, { entidadeId: e.target.value }, false)} aria-label={`Entidade faturadora do item ${n + 1}`} title={ent ? `${d.grupos.find((g) => g.id === ent.grupoId)?.nome ?? ''} · ${ent.fantasia} — ${ent.cnpj}` : undefined} className="selecao-parceiro" data-teste={`entidade-${n + 1}`}>
                            <option value="">— selecionar —</option>
                            {d.grupos
                              .filter((g) => g.situacao === 'Ativo' && g.papeis.some((x) => dados.opcoes.papeis_fornecedor.includes(x)))
                              .map((g) => (
                                <optgroup key={g.id} label={g.nome}>
                                  {d.entidades
                                    .filter((e) => e.grupoId === g.id && (e.situacao === 'Ativa' || e.id === i.entidadeId))
                                    .map((e) => (
                                      <option key={e.id} value={e.id}>
                                        {e.fantasia} — {e.cnpj}
                                      </option>
                                    ))}
                                </optgroup>
                              ))}
                          </select>
                          {editavel && cots.length > 0 && (
                            <select value="" onChange={(e) => e.target.value !== '' && usarCotacao(i.id, Number(e.target.value))} aria-label={`Selecionar cotação do item ${n + 1}`} className="selecionar-cotacao">
                              <option value="">Selecionar cotação…</option>
                              {cots.map(({ c, k }) => (
                                <option key={k} value={k}>
                                  {entidadeDe(d, c.entidadeId)?.fantasia ?? '—'}: {c.moeda === 'USD' ? `US$ ${c.custoUnitMoeda}` : moeda2(c.custoUnitMoeda)} · {c.prazoDias} d
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td>
                          <input type="number" min={0} max={99} step="0.01" value={i.margemDesejada} disabled={!editavel} onChange={(e) => setItem(i.id, { margemDesejada: Number(e.target.value) })} aria-label={`Margem desejada do item ${n + 1}`} className="entrada-curta" data-teste={`margem-desejada-${n + 1}`} />
                        </td>
                        <td>
                          <input type="number" min={0} step="0.01" value={Number.isFinite(i.precoUnit) ? i.precoUnit : 0} disabled={!editavel} onChange={(e) => setItem(i.id, { precoUnit: Number(e.target.value) })} aria-label={`Venda unitária do item ${n + 1}`} title={i.precoManual ? 'Venda unitária ajustada à mão' : 'Venda unitária calculada pela margem desejada'} className="entrada-media" data-teste={`venda-${n + 1}`} />
                        </td>
                        <td className="nowrap">{moeda2(l.vendaTotal)}</td>
                        <td className="nowrap">{moeda2(l.margem)}</td>
                        <td className={`nowrap${Math.abs(dif) >= 0.05 ? (dif < 0 ? ' diferenca-negativa' : ' diferenca-positiva') : ''}`} data-teste={`margem-${n + 1}`} title={Math.abs(dif) >= 0.05 ? `${dif > 0 ? '+' : ''}${dif.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} p.p. vs. margem desejada` : undefined}>
                          {pctNum(efetiva)}
                        </td>
                        {editavel && (
                          <td className="nowrap col-acoes">
                            {/* P4 bloco 3 (D7): ícones com nome acessível e ajuda ao passar o mouse */}
                            <button type="button" className="botao-secundario pequeno botao-icone" title={`Duplicar o item ${n + 1}`} aria-label={`Duplicar o item ${n + 1}`} onClick={() => set({ itens: [...rasc.itens.slice(0, n + 1), { ...i, id: novoId('i') }, ...rasc.itens.slice(n + 1)] })} data-teste={`duplicar-item-${n + 1}`}>
                              ⧉
                            </button>{' '}
                            <button type="button" className="botao-secundario pequeno botao-icone" title={`Remover o item ${n + 1} (até salvar, “Descartar alterações” desfaz)`} aria-label={`Remover o item ${n + 1}`} onClick={() => set({ itens: rasc.itens.filter((x) => x.id !== i.id) })} data-teste={`remover-item-${n + 1}`}>
                              ✕
                            </button>
                          </td>
                        )}
                      </tr>
                      {aberto && (
                        <tr className="linha-detalhe-item" id={`detalhe-item-${i.id}`} data-teste={`detalhe-${n + 1}`}>
                          <td />
                          <td colSpan={editavel ? 13 : 12}>
                            {/* D11: campos fora da linha principal — continuam gravados, no PDF e editáveis quando permitido */}
                            <div className="grade-detalhe-item">
                              <span>
                                <span className="rotulo-campo">Fabricante</span>
                                {pr.fabricante || '—'}
                              </span>
                              <span>
                                <span className="rotulo-campo">Unidade</span>
                                {pr.unidade || '—'}
                              </span>
                              <span>
                                <span className="rotulo-campo">Parceiro comercial</span>
                                {ent ? (d.grupos.find((g) => g.id === ent.grupoId)?.nome ?? '—') : '—'}
                              </span>
                              <label>
                                Disponibilidade
                                <input value={i.disponibilidade} disabled={!editavel} onChange={(e) => setItem(i.id, { disponibilidade: e.target.value }, false)} aria-label={`Disponibilidade do item ${n + 1}`} className="entrada-media" />
                              </label>
                              {conversao && (
                                <span data-teste={`conversao-${n + 1}`}>
                                  <span className="rotulo-campo">Custo unitário em R$</span>
                                  {conversao}
                                </span>
                              )}
                              {i.produtoCopia && (
                                <span title="Cópia histórica gravada no envio">
                                  <span className="rotulo-campo">Produto</span>
                                  cópia da versão
                                </span>
                              )}
                              <span>
                                <span className="rotulo-campo">Categoria · Pilar · Tipo</span>
                                {pr.categoria} · {pr.pilar} · {pr.tipoItem}
                              </span>
                              {i.prazoDias > 0 && (
                                <span title="Dado gravado antes do Prazo geral; não entra na apresentação principal">
                                  <span className="rotulo-campo">Prazo do item (registro anterior)</span>
                                  {i.prazoDias} dias
                                </span>
                              )}
                              <label className="detalhe-observacao">
                                Observação
                                <input value={i.observacao} disabled={!editavel} onChange={(e) => setItem(i.id, { observacao: e.target.value }, false)} aria-label={`Observação do item ${n + 1}`} className="entrada-media" />
                              </label>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {v.itens.length === 0 && !editavel && (
                    <tr>
                      <td colSpan={14} className="texto-secundario">
                        Nenhum item.
                      </td>
                    </tr>
                  )}
                  {editavel && (
                    <tr className="linha-nova" data-teste="linha-novo-item">
                      <td aria-hidden>+</td>
                      <ProdutoNaLinha key={v.itens.length} pn="" descricao="" aoEscolher={adicionarItem} teste="novo-item" rotuloLinha="novo item" />
                      <td colSpan={11} aria-hidden />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <CotacoesVenda v={v} editavel={editavel} set={set} selecionar={selecionar} eu={eu} comparar={painel === 'cotacoes'} />
        </>
      )}
      {v.locacao && <EditorLocacao v={v} editavel={editavel} set={set} propostaId={p.id} alterado={alterado} comparar={painel === 'cotacoes'} />}
      {v.tradein && <EditorTradein v={v} editavel={editavel} set={set} propostaId={p.id} alterado={alterado} comparar={painel === 'cotacoes'} />}
      {painel && <ConsultasProposta proposta={p} v={v} aba={painel} setAba={setPainel} />}

      <ResultadoLeitura v={v} />
      <div className="rodape-acoes-proposta">
        <div className="acoes-direita" data-teste="acoes-proposta">
          {pode('criar', 'atividade') && (
            <button type="button" className="botao-secundario" onClick={() => setAtividade(true)} data-teste="registrar-atividade-proposta">
              Registrar atividade
            </button>
          )}
          <button type="button" className="botao-secundario" onClick={() => setPainel(painel === 'versoes' ? null : 'versoes')} aria-pressed={painel === 'versoes'} data-teste="comparar-versoes">
            Comparar
          </button>
          <button type="button" className="botao-secundario" onClick={() => setPainel(painel === 'historico' ? null : 'historico')} aria-pressed={painel === 'historico'} data-teste="historico-versoes">
            Histórico
          </button>
          <a className="botao-secundario" href={`#${base}/pdf/${v.numero}`} title={alterado ? 'O PDF mostra a versão salva' : undefined} data-teste="visualizar-pdf">
            Visualizar PDF
          </a>
          <BotaoExcluir entidade="proposta" id={p.id} aoExcluir={depoisDeExcluir(`/oportunidades/${p.oportunidadeId}`)} />
          {editavel && (
            <>
              <button type="button" className="botao-secundario" disabled={!alterado || ocupado} onClick={() => setRasc(structuredClone(salvo))}>
                Descartar alterações
              </button>
              <button type="button" className="botao-primario" disabled={!alterado || ocupado} onClick={() => void salvar()} data-teste="salvar-versao">
                Salvar versão
              </button>
              {pode('enviar', 'proposta') && (
                <button type="button" className={podeEnviar(rasc, d) && !alterado ? 'botao-primario' : 'botao-secundario'} disabled={alterado || !podeEnviar(rasc, d) || ocupado} title={alterado ? 'Salve a versão antes de enviar' : !podeEnviar(rasc, d) ? 'Veja o que falta abaixo' : undefined} onClick={() => void enviar()} data-teste="enviar-cliente">
                  Enviar ao Cliente
                </button>
              )}
            </>
          )}
          {v.situacao !== 'rascunho' && eVigente && aberta && pode('registrar_resultado', 'oportunidade') && (
            <button type="button" className="botao-primario" onClick={() => setResultado(true)} data-teste="registrar-resultado-proposta">
              Registrar resultado 🔒
            </button>
          )}
          {v.situacao !== 'rascunho' && eVigente && aberta && !rascunhoDe(p) && pode('editar', 'proposta') && (
            <button type="button" className="botao-primario" disabled={ocupado} onClick={() => void gerarNovaVersao()} data-teste="gerar-nova-versao">
              Gerar nova versão
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Faturamento e entrega. `parte` (Venda, P2-26): "faturamento" vai para "Proposta e faturamento"; "entrega" fica ao lado. */
function FaturamentoEntrega({ v, editavel, set, locais, entidadesCliente, parte }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void; locais: ReturnType<typeof locaisDaVersao>; entidadesCliente: EntidadeClienteRef[]; parte?: 'faturamento' | 'entrega' }) {
  const { aceitarFicticio } = useAmbiente();
  const e = v.entrega;
  const modo = e.mesmoFaturamento ? 'mesmo' : e.entidadeId ? 'entidade' : 'especifico';
  const esp = e.especifica ?? { cnpj: '', endereco: { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' } };
  const setEsp = (patch: Partial<Endereco>, cnpj = esp.cnpj) => set({ entrega: { ...e, mesmoFaturamento: false, entidadeId: undefined, especifica: { cnpj, endereco: { ...esp.endereco, ...patch } } } });
  const faturamento = (
    <>
      <label className="coluna-dupla">
        Filial/unidade de faturamento
        <select value={v.faturamentoEntidadeId} onChange={(ev) => set({ faturamentoEntidadeId: ev.target.value })} data-teste="entidade-faturamento">
          {entidadesCliente.map((x) => (
            <option key={x.id} value={x.id}>
              {x.tipo}: {x.razao} — {x.cnpj}
            </option>
          ))}
        </select>
      </label>
      <div className="largura-total texto-secundario">Endereço de faturamento: {enderecoTexto(locais.faturamento.endereco)}</div>
    </>
  );
  if (parte === 'faturamento') return faturamento;
  return (
    <fieldset className="form-grade" disabled={!editavel} data-teste="faturamento-entrega">
      <legend>{parte === 'entrega' ? (v.locacao ? 'Local de instalação' : 'Entrega') : v.locacao ? 'Faturamento e local de instalação (definidos na Proposta)' : 'Faturamento e entrega (definidos na Proposta)'}</legend>
      {parte !== 'entrega' && faturamento}
      <fieldset className="opcoes grupo-radio largura-total">
        <legend>Entrega no mesmo endereço do faturamento?</legend>
        <label className="opcao">
          <input type="radio" name="entrega-mesmo" checked={modo === 'mesmo'} onChange={() => set({ entrega: { ...e, mesmoFaturamento: true, entidadeId: undefined, especifica: undefined } })} /> Sim
        </label>
        <label className="opcao">
          <input type="radio" name="entrega-mesmo" checked={modo === 'entidade'} onChange={() => set({ entrega: { ...e, mesmoFaturamento: false, entidadeId: entidadesCliente.find((x) => x.id !== v.faturamentoEntidadeId)?.id ?? entidadesCliente[0].id, especifica: undefined } })} /> Outra filial ou unidade
        </label>
        <label className="opcao">
          <input type="radio" name="entrega-mesmo" checked={modo === 'especifico'} onChange={() => setEsp({})} data-teste="entrega-especifica" /> Endereço específico da operação
        </label>
      </fieldset>
      {modo === 'entidade' && (
        <label className="coluna-dupla">
          Filial/unidade de entrega
          <select value={e.entidadeId} onChange={(ev) => set({ entrega: { ...e, entidadeId: ev.target.value } })} data-teste="entidade-entrega">
            {entidadesCliente.map((x) => (
              <option key={x.id} value={x.id}>
                {x.tipo}: {x.razao} — {x.cnpj}
              </option>
            ))}
          </select>
        </label>
      )}
      {modo === 'especifico' && (
        <>
          <CampoCnpj rotulo="CNPJ de entrega" valor={esp.cnpj} aoMudar={(x) => setEsp({}, x)} teste="cnpj-entrega" aceitarFicticio={aceitarFicticio} />
          <CampoCep valor={esp.endereco.cep} aoMudar={(cep) => setEsp({ cep })} teste="cep-entrega" />
          <label className="coluna-dupla">
            Logradouro
            <input value={esp.endereco.logradouro} onChange={(ev) => setEsp({ logradouro: ev.target.value })} />
          </label>
          <label>
            Número
            <input value={esp.endereco.numero} onChange={(ev) => setEsp({ numero: ev.target.value })} />
          </label>
          <label>
            Complemento
            <input value={esp.endereco.complemento} onChange={(ev) => setEsp({ complemento: ev.target.value })} />
          </label>
          <label>
            Bairro
            <input value={esp.endereco.bairro} onChange={(ev) => setEsp({ bairro: ev.target.value })} />
          </label>
          <label>
            Cidade
            <input value={esp.endereco.cidade} onChange={(ev) => setEsp({ cidade: ev.target.value })} />
          </label>
          <SeletorUf valor={esp.endereco.uf} aoMudar={(uf) => setEsp({ uf })} teste="uf-entrega" />
          <p className="texto-secundario largura-total">A consulta automática de CEP aguarda a definição da fonte (pendência da DEMO-03): preencha o endereço manualmente.</p>
        </>
      )}
      <label>
        Contato de entrega
        <input value={e.contato} onChange={(ev) => set({ entrega: { ...e, contato: ev.target.value } })} />
      </label>
      <label>
        Telefone de entrega
        <input value={e.telefone} onChange={(ev) => set({ entrega: { ...e, telefone: ev.target.value } })} />
      </label>
      <div className="largura-total resumo-entrega" data-teste="resumo-entrega">
        <strong>Entrega:</strong> {locais.entrega.origem} · CNPJ {locais.entrega.cnpj} · {enderecoTexto(locais.entrega.endereco)} · Contato {locais.entrega.contato || '—'} {locais.entrega.telefone}
      </div>
    </fieldset>
  );
}

/* =====================================================================
 * Venda — cotações por fornecedor. Só a cotação SELECIONADA de cada item
 * alimenta custo, entidade, prazo e PTAX.
 * ===================================================================== */
const dataHoraBr = (iso: string) => {
  const [dia, hora] = iso.split('T');
  return `${dataBr(dia)}${hora ? ` às ${hora.slice(0, 5)}` : ''}`;
};

/** PTAX de venda (F1-12, seção 5): a explicação longa saiu do corpo; fica a ajuda "?" no título das cotações. */
// P3-21: explicação fixa das cotações vai para a ajuda; texto alinhado à PTAX da proposta (pacote 1), sem mudar versões congeladas
const AJUDA_COTACOES =
  'Só a cotação selecionada de cada item alimenta custo, entidade faturadora e prazo — e, daí, os cálculos e documentos. Em USD, vale a PTAX da proposta; a PTAX informada numa cotação fica só como histórico. Trocar a selecionada nunca apaga as outras.';
const AJUDA_COTACOES_ANTIGAS =
  'Versão anterior à PTAX da proposta: cada cotação em USD tem a sua PTAX, congelada no envio. Só a cotação selecionada de cada item alimenta custo, entidade faturadora, prazo e PTAX. Trocar a selecionada nunca apaga as outras.';
function CelulaPtax({ c, editavel, aoEditar, k }: { c: Cotacao; editavel: boolean; aoEditar: () => void; k: number }) {
  if (c.moeda !== 'USD') return <span className="texto-secundario">não se aplica (BRL)</span>;
  const p = c.ptax;
  const valor = ptaxUtilizada(p);
  return (
    <div className="celula-ptax" data-teste={`ptax-cotacao-${k + 1}`}>
      <strong className={valor && valor > 0 ? '' : 'diferenca-negativa'}>{valor && valor > 0 ? ptax4(valor) : 'PTAX obrigatória'}</strong>
      {p?.manual ? (
        <>
          <span className="selo selo-ptax-manual">{p.oficial ? 'PTAX alterada manualmente' : 'PTAX informada'}</span>
          <span className="texto-secundario">
            {p.oficial ? `oficial ${ptax4(p.oficial.valor)} · ` : ''}
            {p.manual.origem} · ref. {dataBr(p.manual.dataReferencia)}
          </span>
        </>
      ) : (
        p?.oficial && <span className="texto-secundario">oficial · fech. {dataBr(p.oficial.dataReferencia)}</span>
      )}
      <button type="button" className="botao-link" onClick={aoEditar} aria-label={`${editavel ? 'Informar' : 'Ver'} PTAX da cotação ${k + 1}`} data-teste={`editar-ptax-${k + 1}`}>
        {editavel ? (valor ? 'Alterar PTAX' : 'Informar PTAX') : 'Ver PTAX'}
      </button>
    </div>
  );
}

function ModalPtax({ c, editavel, aoSalvar, aoFechar }: { c: Cotacao; editavel: boolean; aoSalvar: (p: PtaxCotacao) => void; aoFechar: () => void }) {
  const { dados, eu } = useProposta();
  const { pode } = useSessao();
  const hoje = dados.opcoes.hoje;
  const podeManual = editavel && pode('editar_ptax', 'proposta');
  const [consulta, setConsulta] = useState('');
  const p = c.ptax ?? novaPtax();
  const consultarOficial = async () => {
    setConsulta('Consultando a PTAX oficial…');
    try {
      const r = await api.get<RespostaPtax>('/api/ptax');
      if (!r.disponivel) return setConsulta(r.motivo);
      aoSalvar({ ...p, oficial: r.oficial, historico: [...p.historico, { data: agoraSaoPaulo(), texto: `PTAX oficial consultada: ${ptax4(r.oficial.valor)} (fechamento de ${dataBr(r.oficial.dataReferencia)})${r.aviso ? ` — ${r.aviso}` : ''}`, autor: eu }] });
    } catch (e) {
      setConsulta(`Não foi possível consultar: ${(e as Error).message}`);
    }
  };
  const [valor, setValor] = useState(String(p.manual?.valor ?? p.oficial?.valor ?? ''));
  const [origem, setOrigem] = useState<OrigemPtaxManual | ''>(p.manual?.origem ?? '');
  const [dataRef, setDataRef] = useState(p.manual?.dataReferencia ?? p.oficial?.dataReferencia ?? hoje);
  const [justificativa, setJustificativa] = useState('');
  const m = { valor: Number(valor.replace(',', '.')), origem: origem || undefined, dataReferencia: dataRef, justificativa };
  const erros = validarPtaxManual(m, hoje);
  return (
    <Modal
      titulo={`PTAX da cotação — ${c.moeda}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {editavel && (
            <button type="button" className="botao-secundario" onClick={() => void consultarOficial()} data-teste="consultar-ptax">
              {p.oficial ? 'Consultar de novo' : 'Consultar PTAX oficial'}
            </button>
          )}
          {podeManual && p.manual && p.oficial && (
            <button type="button" className="botao-secundario" onClick={() => aoSalvar(restaurarPtaxOficial(p, eu, agoraSaoPaulo()))} data-teste="restaurar-ptax">
              Voltar à PTAX oficial
            </button>
          )}
          {podeManual && (
            <button type="button" className="botao-primario" disabled={erros.length > 0} onClick={() => aoSalvar(substituirPtax(p, { ...(m as PtaxManual), usuario: eu, alteradaEm: agoraSaoPaulo() }))} data-teste="aplicar-ptax">
              {p.manual || p.oficial ? 'Aplicar substituição' : 'Aplicar PTAX'}
            </button>
          )}
        </>
      }
    >
      <dl className="dados" data-teste="ptax-oficial">
        <dt>PTAX oficial consultada</dt>
        <dd>{p.oficial ? `USD venda — fechamento de ${dataBr(p.oficial.dataReferencia)}: ${ptax4(p.oficial.valor)}` : 'não consultada (fonte indisponível no momento da inclusão)'}</dd>
        {p.oficial && (
          <>
            <dt>Fonte</dt>
            <dd>{p.oficial.fonte}</dd>
            <dt>Consultada pelo HUB em</dt>
            <dd>{dataHoraBr(p.oficial.consultadaEm)}</dd>
          </>
        )}
        <dt>PTAX utilizada no cálculo</dt>
        <dd data-teste="ptax-utilizada">
          <strong>{ptax4(ptaxUtilizada(p))}</strong> {p.manual ? <span className="selo selo-ptax-manual">{p.oficial ? 'PTAX alterada manualmente' : 'PTAX informada'}</span> : p.oficial ? <span className="selo selo-neutro">oficial</span> : null}
        </dd>
      </dl>
      {consulta && (
        <p className="aviso aviso-alerta" role="status" data-teste="consulta-ptax">
          {consulta}
        </p>
      )}
      {editavel && !podeManual && <p className="aviso aviso-info">Informar ou alterar a PTAX manualmente exige a permissão “editar PTAX”.</p>}
      {podeManual ? (
        <fieldset className="form-grade" data-teste="form-ptax">
          <legend>{p.oficial ? 'Substituir a PTAX desta cotação (o valor oficial é preservado)' : 'Informar a PTAX desta cotação'}</legend>
          <label>
            {p.oficial ? 'Nova PTAX' : 'PTAX'}
            <input type="number" min={0} step="0.0001" value={valor} onChange={(e) => setValor(e.target.value)} data-teste="ptax-valor" />
          </label>
          <label>
            Origem
            <select value={origem} onChange={(e) => setOrigem(e.target.value as OrigemPtaxManual)} data-teste="ptax-origem">
              <option value="">Selecione…</option>
              {ORIGENS_PTAX.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <CampoData rotulo="Data de referência" valor={dataRef} aoMudar={setDataRef} max={hoje} teste="ptax-data" obrigatorio />
          <label className="largura-total">
            Justificativa (obrigatória)
            <textarea rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="ptax-justificativa" />
          </label>
          {erros.length > 0 && (
            <ul className="aviso aviso-alerta largura-total" data-teste="erros-ptax">
              {erros.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          <p className="texto-secundario largura-total">Fica na trilha da cotação e na auditoria com o seu usuário, a data e a hora do servidor ao salvar a versão.</p>
        </fieldset>
      ) : (
        !editavel && <p className="aviso aviso-info">Versão emitida: a PTAX está congelada. Para mudar, gere uma nova versão.</p>
      )}
      <h3>Trilha da PTAX</h3>
      <ul className="linha-tempo" data-teste="auditoria-ptax">
        {p.historico.map((h, n) => (
          <li key={n}>
            <strong>{dataHoraBr(h.data)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
          </li>
        ))}
        {p.historico.length === 0 && <li className="texto-secundario">Nenhuma PTAX informada nesta cotação.</li>}
      </ul>
    </Modal>
  );
}

function CotacoesVenda({ v, editavel, set, selecionar, eu, comparar }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void; selecionar: (x: Versao, itemId: string, cotacaoId: string) => Versao; eu: string; /** P2-30: "Comparar cotações" aberto no painel de consultas destaca menor custo e menor prazo */ comparar: boolean }) {
  const { d, dados } = useProposta();
  const [ptaxDe, setPtaxDe] = useState<string | null>(null);
  const prod = (id: string) => produtoDoItem(d, { produtoId: id });
  const aplicar = (nv: Versao) => set(sincronizarCotacoes(nv, d));
  const setCot = (id: string, patch: Partial<Cotacao>) => aplicar({ ...v, cotacoes: v.cotacoes.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const produtosDosItens = [...new Set(v.itens.map((i) => i.produtoId))];
  const ativas = d.entidades.filter((e) => e.situacao === 'Ativa' && d.grupos.find((g) => g.id === e.grupoId)?.papeis.some((x) => dados.opcoes.papeis_fornecedor.includes(x)));
  const qtd = (produtoId: string) => v.itens.find((i) => i.produtoId === produtoId)?.quantidade ?? 1;
  // F1-57: no modelo novo, o custo em R$ da cotação usa a PTAX da proposta; a da cotação fica só como histórico (P4)
  const unica = usaPtaxDaProposta(v);
  const custoReais = (c: Cotacao) => (unica && c.moeda === 'USD' ? arred(c.custoUnitMoeda * (ptaxDaProposta(v) ?? 0)) : custoCotacaoReais(c));
  const totalReais = (c: Cotacao) => arred(custoReais(c) * qtd(c.produtoId));
  const melhor = (produtoId: string, f: (c: Cotacao) => number) => Math.min(...v.cotacoes.filter((c) => c.produtoId === produtoId).map(f));
  const ptaxDiferentes = (produtoId: string) => new Set(v.cotacoes.filter((c) => c.produtoId === produtoId && c.moeda === 'USD').map((c) => ptaxUtilizada(c.ptax))).size > 1;
  const emEdicao = v.cotacoes.find((c) => c.id === ptaxDe);
  const reg = (x: Versao, texto: string) => registrarCotacao(x, texto, eu, agoraSaoPaulo());
  const [avisoPtax, setAvisoPtax] = useState('');
  const adicionarCotacao = async (produtoId: string) => {
    const pr = prod(produtoId);
    if (!ativas.length) return;
    let ptax: PtaxCotacao | undefined;
    if (pr.moeda === 'USD') {
      try {
        const r = await api.get<RespostaPtax>('/api/ptax');
        ptax = r.disponivel ? novaPtax(r.oficial) : novaPtax();
        setAvisoPtax(r.disponivel ? (r.aviso ?? '') : r.motivo);
      } catch (e) {
        ptax = novaPtax();
        setAvisoPtax(`PTAX oficial indisponível (${(e as Error).message}). Informe a PTAX manualmente.`);
      }
    }
    const nova: Cotacao = { id: novoId('c'), produtoId, entidadeId: ativas[0].id, moeda: pr.moeda, custoUnitMoeda: 0, prazoDias: 10, frete: 0, observacao: '', ...(ptax ? { ptax } : {}) };
    const ultima = v.cotacoes.map((c) => c.produtoId).lastIndexOf(produtoId);
    const cotacoes = ultima < 0 ? [...v.cotacoes, nova] : [...v.cotacoes.slice(0, ultima + 1), nova, ...v.cotacoes.slice(ultima + 1)];
    aplicar({ ...v, cotacoes, historicoCotacoes: reg(v, `Cotação adicionada para ${pr.sku || pr.nome}${ptax?.oficial ? ` (PTAX oficial ${ptax4(ptax.oficial.valor)} de ${dataBr(ptax.oficial.dataReferencia)})` : pr.moeda === 'USD' ? ' (PTAX a informar)' : ''}`) });
  };
  const grupos = [...new Set([...produtosDosItens, ...v.cotacoes.map((c) => c.produtoId)])].map((produtoId) => ({
    produtoId,
    itens: v.itens.map((i, n) => ({ i, n })).filter(({ i }) => i.produtoId === produtoId),
    cotacoes: v.cotacoes.map((c, k) => ({ c, k })).filter(({ c }) => c.produtoId === produtoId),
  }));
  return (
    // P3-20/P3-21: accordion inicialmente fechado (título, seta e ajuda); ao abrir, mesma tabela, histórico e comparação
    <details className="secao-editor acordeao-cotacoes" data-teste="cotacoes">
      <summary className="barra-ferramentas" data-teste="abrir-cotacoes">
        <h2 className="sem-margem">Cotações por fornecedor</h2>
        <Ajuda titulo="Cotações por fornecedor">{unica ? AJUDA_COTACOES : AJUDA_COTACOES_ANTIGAS}</Ajuda>
      </summary>
      {avisoPtax && (
        <p className="aviso aviso-alerta" role="status" data-teste="aviso-ptax">
          {avisoPtax}
        </p>
      )}
      <div className="rolagem">
        <table className="tabela tabela-compacta tabela-editor tabela-cotacoes" data-teste="tabela-cotacoes">
          <thead>
            <tr>
              <th>PN/SKU</th>
              <th>Qtd</th>
              <th>Moeda</th>
              <th>Custo unitário</th>
              <th>Custo total (R$)</th>
              <th>Entidade faturadora/CNPJ</th>
              <th>Prazo (dias)</th>
              <th>Frete (R$)</th>
              <th>PTAX</th>
              <th>Total em R$</th>
              <th>Observação</th>
              <th>Selecionada</th>
              {editavel && <th>Ações</th>}
            </tr>
          </thead>
          {grupos.map((g) => {
            const pr = prod(g.produtoId);
            const selecionadas = g.itens.map(({ i, n }) => ({ n, c: v.cotacoes.find((c) => c.id === i.cotacaoId) }));
            return (
              <tbody key={g.produtoId} className="grupo-cotacoes" data-teste={`grupo-cotacao-${pr.sku || g.produtoId}`}>
                <tr className="cabecalho-grupo-cotacao">
                  <th colSpan={editavel ? 13 : 12} scope="rowgroup">
                    <div className="grupo-cotacao-linha">
                      <span className="grupo-cotacao-item">
                        {g.itens.length ? `Item ${g.itens.map(({ n }) => n + 1).join(', ')}` : 'Sem item na versão'} · <strong className="pn-grupo">{pr.sku || 'sem PN'}</strong> — {pr.nome}{' '}
                        <span className="texto-secundario">
                          · qtd {qtd(g.produtoId)} · {g.cotacoes.length} cotação(ões): {[...new Set(g.cotacoes.map(({ c }) => entidadeDe(d, c.entidadeId)?.fantasia ?? '—'))].join(', ') || '—'}
                        </span>
                      </span>
                      <span className="grupo-cotacao-alimenta" data-teste={`alimenta-${pr.sku || g.produtoId}`}>
                        {selecionadas.map(({ n, c }) => (
                          <span key={n}>
                            {c ? (
                              <>
                                ★ Alimenta o item {n + 1}: <strong>{entidadeDe(d, c.entidadeId)?.fantasia}</strong> — custo {c.moeda === 'USD' ? `US$ ${c.custoUnitMoeda.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : moeda2(c.custoUnitMoeda)} · prazo {c.prazoDias} d{c.moeda === 'USD' ? ` · PTAX ${ptax4(ptaxUtilizada(c.ptax))}` : ''}
                              </>
                            ) : (
                              <span className="texto-secundario">Item {n + 1}: nenhuma cotação selecionada (custo informado na linha)</span>
                            )}
                          </span>
                        ))}
                      </span>
                      {editavel && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => void adicionarCotacao(g.produtoId)} disabled={!ativas.length} data-teste={`adicionar-cotacao-${pr.sku || g.produtoId}`}>
                          Adicionar cotação
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
                {g.cotacoes.map(({ c, k }) => {
                  const e = entidadeDe(d, c.entidadeId);
                  const itensDoProduto = v.itens.map((i, n) => ({ i, n })).filter(({ i }) => i.produtoId === c.produtoId);
                  const selecionada = itensDoProduto.some(({ i }) => i.cotacaoId === c.id);
                  const q = qtd(c.produtoId);
                  return (
                    <tr key={c.id} className={selecionada ? 'linha-escolhida' : ''} data-teste={`cotacao-${k + 1}`}>
                      <td className="nowrap" title={`${pr.nome} — ${pr.fabricante}`}>
                        {pr.sku}
                      </td>
                      <td>{q}</td>
                      <td>{c.moeda}</td>
                      <td>
                        <input type="number" min={0} step="0.01" value={c.custoUnitMoeda} disabled={!editavel} onChange={(ev) => setCot(c.id, { custoUnitMoeda: Number(ev.target.value) })} aria-label={`Custo da cotação ${k + 1}`} className="entrada-media" data-teste={`custo-cotacao-${k + 1}`} />
                        {c.moeda === 'USD' && <div className="texto-secundario nowrap">= {moeda2(custoReais(c))} un.{unica ? ' (PTAX da proposta)' : ''}</div>}
                      </td>
                      <td className="nowrap">{c.moeda === 'USD' ? `US$ ${(c.custoUnitMoeda * q).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : moeda2(c.custoUnitMoeda * q)}</td>
                      <td>
                        <select value={c.entidadeId} disabled={!editavel} onChange={(ev) => setCot(c.id, { entidadeId: ev.target.value })} aria-label={`Entidade da cotação ${k + 1}`} data-teste={`entidade-cotacao-${k + 1}`}>
                          {[...ativas, ...(e && !ativas.includes(e) ? [e] : [])].map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.fantasia}
                            </option>
                          ))}
                        </select>
                        <div className="texto-secundario nowrap" title={e ? `Parceiro comercial: ${d.grupos.find((x) => x.id === e.grupoId)?.nome ?? '—'}` : undefined}>{e?.cnpj}</div>
                      </td>
                      <td>
                        <input type="number" min={0} value={c.prazoDias} disabled={!editavel} onChange={(ev) => setCot(c.id, { prazoDias: Math.max(0, Math.floor(Number(ev.target.value)) || 0) })} aria-label={`Prazo da cotação ${k + 1}`} className="entrada-curta" />
                        {comparar && c.prazoDias === melhor(c.produtoId, (x) => x.prazoDias) && <div className="diferenca-positiva">menor prazo</div>}
                      </td>
                      <td>
                        <input type="number" min={0} step="0.01" value={c.frete ?? 0} disabled={!editavel} onChange={(ev) => setCot(c.id, { frete: Number(ev.target.value) })} aria-label={`Frete da cotação ${k + 1}`} className="entrada-curta" />
                      </td>
                      <td>
                        {unica ? (
                          <span className="texto-secundario" title="PTAX informada na cotação: guardada só como histórico (P4)" data-teste={`ptax-historico-${k + 1}`}>
                            {c.moeda === 'USD' ? (ptaxUtilizada(c.ptax) ? `cotação: ${ptax4(ptaxUtilizada(c.ptax)!)}` : 'PTAX da proposta') : `não se aplica (${c.moeda})`}
                          </span>
                        ) : (
                          <CelulaPtax c={c} editavel={editavel} aoEditar={() => setPtaxDe(c.id)} k={k} />
                        )}
                        {comparar && c.moeda === 'USD' && ptaxDiferentes(c.produtoId) && <div className="diferenca-negativa">PTAX diferente entre cotações</div>}
                      </td>
                      <td className="nowrap" data-teste={`total-reais-${k + 1}`}>
                        {moeda2(totalReais(c))}
                        {comparar && totalReais(c) === melhor(c.produtoId, totalReais) && <div className="diferenca-positiva">menor custo</div>}
                      </td>
                      <td>
                        <input value={c.observacao ?? ''} disabled={!editavel} onChange={(ev) => setCot(c.id, { observacao: ev.target.value })} aria-label={`Observação da cotação ${k + 1}`} className="entrada-media" />
                      </td>
                      <td>
                        {itensDoProduto.map(({ i, n }) => (
                          <label key={i.id} className="opcao nowrap">
                            <input type="radio" name={`sel-${i.id}`} checked={i.cotacaoId === c.id} disabled={!editavel} onChange={() => set(selecionar(v, i.id, c.id))} aria-label={`Selecionar a cotação ${k + 1} para o item ${n + 1}`} data-teste={`selecionar-cotacao-${k + 1}-item-${n + 1}`} /> item {n + 1}
                          </label>
                        ))}
                        {selecionada && <span className="selo selo-destaque">★ selecionada</span>}
                      </td>
                      {editavel && (
                        <td className="nowrap">
                          <button type="button" className="botao-secundario pequeno" onClick={() => aplicar({ ...v, cotacoes: [...v.cotacoes.slice(0, k + 1), { ...c, id: novoId('c'), ...(c.ptax ? { ptax: structuredClone(c.ptax) } : {}) }, ...v.cotacoes.slice(k + 1)], historicoCotacoes: reg(v, `Cotação ${k + 1} duplicada`) })}>
                            Duplicar
                          </button>{' '}
                          <button type="button" className="botao-secundario pequeno" onClick={() => aplicar({ ...v, cotacoes: v.cotacoes.filter((x) => x.id !== c.id), historicoCotacoes: reg(v, `Cotação ${k + 1} removida${selecionada ? ' (era a selecionada: o item ficou sem cotação)' : ''}`) })}>
                            Remover
                          </button>
                        </td>
                      )}

                    </tr>
                  );
                })}
              </tbody>
            );
          })}
          {grupos.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={13} className="texto-secundario">
                  Nenhum item na versão. Inclua os itens: cada um ganha o seu grupo de cotações.
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      {emEdicao && (
        <ModalPtax
          c={emEdicao}
          editavel={editavel}
          aoFechar={() => setPtaxDe(null)}
          aoSalvar={(np) => {
            aplicar({ ...v, cotacoes: v.cotacoes.map((x) => (x.id === emEdicao.id ? { ...x, ptax: np } : x)), historicoCotacoes: reg(v, `PTAX da cotação ${v.cotacoes.indexOf(emEdicao) + 1} ${np.manual ? `informada: ${ptax4(ptaxUtilizada(np))}` : 'voltou à oficial'}`) });
            setPtaxDe(null);
          }}
        />
      )}
    </details>
  );
}

/* =====================================================================
 * Condições (Venda e Trade-in) — lista PARAMETRIZADA.
 * ===================================================================== */
function CondicoesComuns({ v, editavel, set }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void }) {
  const { d, dados } = useProposta();
  const tipo = tipoDaVersao(v);
  if (tipo === 'trade_in') return <RecebimentoTradein v={v} editavel={editavel} set={set} />;
  const lista = dados.opcoes.condicoes_pagamento;
  const total = economiaVersao(d, v).totalFinal;
  const parcelas = parcelasDaCondicao(lista, v.condicoes.condicaoId, total, v.data);
  return (
    <fieldset className="form-grade" disabled={!editavel} data-teste="condicoes">
      <legend>
        Condições de pagamento
        <Ajuda titulo="Condições de pagamento">Lista parametrizada (Parâmetros comerciais). A prévia das parcelas é só informativa: nenhum lançamento financeiro é gerado aqui. No futuro, a condição alimentará Contas a Receber, vencimentos, fluxo de caixa e comissionamento.</Ajuda>
      </legend>
      {/* P3-19: a Venda não mostra "Forma de pagamento" (o dado antigo é preservado e segue nos documentos já emitidos) */}
      <label>
        Condição de pagamento
        <select value={v.condicoes.condicaoId} onChange={(e) => set({ condicoes: { ...v.condicoes, condicaoId: e.target.value, condicao: lista.find((c) => c.id === e.target.value)?.nome ?? '' } })} data-teste="condicao-pagamento">
          {!lista.some((c) => c.id === v.condicoes.condicaoId) && <option value={v.condicoes.condicaoId}>{v.condicoes.condicao || '— selecione —'}</option>}
          {lista.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
      </label>
      <label>
        Prazo geral
        <input value={v.condicoes.prazoGeral} onChange={(e) => set({ condicoes: { ...v.condicoes, prazoGeral: e.target.value } })} />
      </label>
      <label className="largura-total">
        Observação
        <textarea rows={2} value={v.condicoes.observacao} onChange={(e) => set({ condicoes: { ...v.condicoes, observacao: e.target.value } })} />
      </label>
      <div className="largura-total texto-secundario" data-teste="previa-parcelas">
        {lista.length === 0 ? (
          <span>A lista de condições de pagamento não está configurada neste ambiente (parâmetro comercial.condicoes_pagamento).</span>
        ) : parcelas.length ? (
          <span>
            Prévia ({v.condicoes.condicao}): {parcelas.map((x) => `${x.n}ª ${dataBr(x.vencimento)} ${moeda2(x.valor)}`).join(' · ')}.
          </span>
        ) : total <= 0 ? (
          <span>Sem valor para gerar parcelas.</span>
        ) : (
          <span>Prévia indisponível: a condição selecionada não tem vencimentos configurados.</span>
        )}
      </div>
      {/* Pacote 5: os prazos da condição são copiados no envio (Venda); sem prazos, o envio avisa e não bloqueia */}
      {tipo === 'venda' && editavel && lista.length > 0 && !parcelas.length && total > 0 && (
        <p className="largura-total aviso aviso-info" data-teste="aviso-sem-prazos">
          Esta condição não tem prazos estruturados: ao enviar, o calendário do Cliente ficará a informar no Financeiro (Contas a Receber). O envio não é bloqueado.
        </p>
      )}
      {tipo === 'venda' && !editavel && v.condicoes.prazos !== undefined && (
        <p className="largura-total texto-secundario" data-teste="prazos-congelados-proposta">
          {v.condicoes.prazos
            ? `Prazos congelados no envio (${dataBr(v.condicoes.prazos.copiadoEm)}): ${v.condicoes.prazos.dias.length} parcela(s) · ${v.condicoes.prazos.dias.join('/')} dias da NF do parceiro, partes iguais, resíduo na última. Mudar o cadastro depois não os altera.`
            : 'Condição sem prazos estruturados no envio: o calendário do Cliente fica a informar no Financeiro.'}
        </p>
      )}
    </fieldset>
  );
}

/**
 * Pacote 4 (bloco 2): Trade-in — o Cliente recebe sempre por transferência bancária; só o prazo (dias) é informado.
 * Versões enviadas antes disso mostram a forma e a condição como foram gravadas (registro anterior).
 */
function RecebimentoTradein({ v, editavel, set }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void }) {
  const rec = recebimentoTradein(v.condicoes);
  const anterior = rec.anterior && !editavel;
  return (
    <fieldset className="form-grade" disabled={!editavel} data-teste="condicoes">
      <legend>
        Condições de recebimento pelo Cliente
        <Ajuda titulo="Recebimento do Cliente">O valor destinado ao Cliente é recebido por transferência bancária. Informe em quantos dias e a partir de qual evento, conforme a negociação com o comprador. O vencimento só existe depois do registro da data efetiva desse evento; nenhum lançamento financeiro é gerado aqui. Não se refere à comissão da Inttrix.</Ajuda>
      </legend>
      {anterior ? (
        <div className="largura-total" data-teste="recebimento-anterior">
          <span className="rotulo-campo">Recebimento do Cliente (registro anterior)</span>
          <div>
            {rec.recebimento} · {rec.prazo}
          </div>
        </div>
      ) : (
        <>
          <div data-teste="recebimento-fixo">
            <span className="rotulo-campo">Recebimento do Cliente</span>
            <div className="valor-fixo">{RECEBIMENTO_TRADEIN}</div>
          </div>
          <label>
            Prazo de recebimento (dias)
            <input
              type="number"
              min={0}
              max={3650}
              step={1}
              inputMode="numeric"
              value={typeof v.condicoes.prazoRecebimentoDias === 'number' ? v.condicoes.prazoRecebimentoDias : ''}
              onChange={(e) => set({ condicoes: { ...v.condicoes, forma: RECEBIMENTO_TRADEIN, condicaoId: '', condicao: '', prazoRecebimentoDias: e.target.value === '' ? null : Math.max(0, Math.trunc(Number(e.target.value))) } })}
              data-teste="prazo-recebimento-dias"
            />
          </label>
          <label>
            Contados a partir de
            <select value={v.condicoes.marcoRecebimento ?? ''} onChange={(e) => set({ condicoes: { ...v.condicoes, marcoRecebimento: (e.target.value || null) as MarcoRecebimento | null, marcoRecebimentoDescricao: e.target.value === 'outro' ? (v.condicoes.marcoRecebimentoDescricao ?? '') : '' } })} data-teste="marco-recebimento">
              <option value="">— selecione —</option>
              {MARCOS_RECEBIMENTO.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
          </label>
          {v.condicoes.marcoRecebimento === 'outro' && (
            <label className="largura-total">
              Descrição do marco <span className="texto-secundario">(obrigatória quando Outro)</span>
              <input value={v.condicoes.marcoRecebimentoDescricao ?? ''} maxLength={200} placeholder="Descreva o marco combinado" onChange={(e) => set({ condicoes: { ...v.condicoes, marcoRecebimentoDescricao: e.target.value } })} data-teste="marco-recebimento-descricao" />
            </label>
          )}
        </>
      )}
      <label>
        Prazo geral
        <input value={v.condicoes.prazoGeral} onChange={(e) => set({ condicoes: { ...v.condicoes, prazoGeral: e.target.value } })} />
      </label>
      <label className="largura-total">
        Observação
        <textarea rows={2} value={v.condicoes.observacao} onChange={(e) => set({ condicoes: { ...v.condicoes, observacao: e.target.value } })} />
      </label>
      <p className="largura-total texto-secundario sem-margem">Trade-in: valor a RECEBER pelo Cliente na alienação dos equipamentos — não é venda de produtos pela Inttrix.</p>
    </fieldset>
  );
}

/** P2-30: consultas da proposta num só painel (abre abaixo das Cotações, oculto até ser acionado). */
type Consulta = 'versoes' | 'cotacoes' | 'historico' | 'historico-cotacoes' | 'ptax';
function ConsultasProposta({ proposta: p, v, aba, setAba }: { proposta: TProposta; v: Versao; aba: Consulta; setAba: (a: Consulta | null) => void }) {
  const tipo = tipoDaVersao(v);
  // revisão 12/10: Locação e Trade-in também comparam e consultam histórico pelo painel único (sem botão "Comparar" próprio)
  const nomeCotacoes = tipo === 'venda' ? 'cotações' : tipo === 'locacao' ? 'propostas de parceiros' : 'avaliações';
  const abas: { id: Consulta; rotulo: string }[] = [
    { id: 'versoes', rotulo: 'Comparar versões' },
    { id: 'cotacoes', rotulo: `Comparar ${nomeCotacoes}` },
    { id: 'historico', rotulo: 'Histórico da proposta' },
    { id: 'historico-cotacoes', rotulo: tipo === 'venda' ? 'Histórico das cotações' : tipo === 'locacao' ? 'Histórico do parceiro e da regra' : 'Histórico das avaliações' },
    ...(v.ptaxProposta ? [{ id: 'ptax' as const, rotulo: 'Histórico da PTAX' }] : []),
  ];
  const historicoCotacoes: { data: string; texto: string; autor: string }[] = [
    ...(v.historicoCotacoes ?? []),
    ...v.cotacoes.flatMap((c) => c.ptax?.historico ?? []),
    ...(v.locacao?.regraComissao?.historico ?? []),
    ...(v.locacao?.historicoReajuste ?? []),
    ...(v.tradein?.importacoes ?? []).map((i) => ({ data: i.dataHora, texto: `Inventário importado de ${i.arquivo}: ${i.validas} válida(s), ${i.invalidas} inválida(s), ${i.duplicadas} duplicada(s), ${i.ignoradas} ignorada(s)`, autor: i.usuario })),
  ].sort((x, y) => x.data.localeCompare(y.data));
  return (
    <section className="secao-editor painel-consultas" data-teste="painel-consultas" aria-label="Consultas da proposta">
      <div className="barra-ferramentas">
        <h2 className="sem-margem">Consultas da proposta</h2>
        <span className="espaco" />
        <button type="button" className="botao-link" onClick={() => setAba(null)} data-teste="fechar-consultas">
          ✕ Fechar
        </button>
      </div>
      <div className="abas abas-consulta" role="tablist" aria-label="Consultas">
        {abas.map((x) => (
          <button key={x.id} type="button" role="tab" aria-selected={aba === x.id} className={aba === x.id ? 'ativa' : ''} onClick={() => setAba(x.id)} data-teste={`aba-consulta-${x.id}`}>
            {x.rotulo}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {aba === 'versoes' && <CompararVersoes proposta={p} />}
        {aba === 'historico' && <HistoricoVersoes proposta={p} v={v} />}
        {aba === 'cotacoes' && (
          <p className="texto-secundario" data-teste="nota-comparar-cotacoes">
            {tipo === 'venda'
              ? 'Na tabela de Cotações acima, o menor custo (total em R$) e o menor prazo de cada item ficam destacados, e PTAX diferentes entre cotações do mesmo item são sinalizadas.'
              : tipo === 'locacao'
                ? 'Na tabela de Propostas de parceiros acima, a de menor valor total ao Cliente fica destacada.'
                : 'Na tabela de Avaliações acima, a de maior valor ao Cliente fica destacada.'}
          </p>
        )}
        {aba === 'historico-cotacoes' && (
          <ul className="linha-tempo" data-teste="lista-historico-cotacoes">
            {historicoCotacoes.map((h, n) => (
              <li key={n}>
                <strong>{dataHoraBr(h.data)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
              </li>
            ))}
            {!historicoCotacoes.length && <li className="texto-secundario">Sem alterações de {nomeCotacoes} nesta versão. Versões anteriores preservam os dados da época.</li>}
          </ul>
        )}
        {aba === 'ptax' && v.ptaxProposta && (
          <ul className="linha-tempo" data-teste="lista-historico-ptax-proposta">
            {v.ptaxProposta.historico
              .slice()
              .reverse()
              .map((h, n) => (
                <li key={n}>
                  <strong>{dataHoraBr(h.data)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Comparar as versões da Proposta (totais, margem/comissão, validade e situação). */
function CompararVersoes({ proposta: p }: { proposta: TProposta }) {
  const { d } = useProposta();
  const vs = p.versoes;
  return (
    <section className="cartao" data-teste="painel-comparar">
      <h2>Comparar versões</h2>
      <div className="rolagem">
        <table className="tabela tabela-compacta">
          <thead>
            <tr>
              <th>Versão</th>
              <th>Status</th>
              <th>Data</th>
              <th>Validade</th>
              <th>Total ao Cliente</th>
              <th>Diferença para a anterior</th>
              <th>Margem/comissão</th>
            </tr>
          </thead>
          <tbody>
            {vs.map((x, i) => {
              const e = economiaVersao(d, x);
              const ant = i > 0 ? economiaVersao(d, vs[i - 1]).totalFinal : undefined;
              return (
                <tr key={x.numero}>
                  <td>v{x.numero}</td>
                  <td>{SITUACOES_VERSAO[x.situacao]}</td>
                  <td>{dataBr(x.data)}</td>
                  <td>{dataBr(x.validade)}</td>
                  <td className="nowrap">{moeda2(e.totalFinal)}</td>
                  <td className="nowrap">{ant === undefined ? '—' : `${e.totalFinal - ant >= 0 ? '+' : ''}${moeda2(e.totalFinal - ant)}`}</td>
                  <td className="nowrap">
                    {moeda2(e.bruta)} ({pct(e.percentual)})
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Histórico da Proposta: versões, cotações, PTAX e regra de comissão (usuário, data e hora). */
function HistoricoVersoes({ proposta: p, v }: { proposta: TProposta; v: Versao }) {
  const { nomeUsuario } = useProposta();
  const eventos = [
    ...p.versoes.flatMap((x) => [{ data: x.data, texto: `v${x.numero} criada (${SITUACOES_VERSAO[x.situacao]})`, autor: nomeUsuario(x.responsavel) }, ...(x.enviadaEm ? [{ data: x.enviadaEm, texto: `v${x.numero} enviada ao Cliente`, autor: nomeUsuario(x.responsavel) }] : [])]),
    ...(tipoDaVersao(v) === 'venda' ? [] : [...(v.historicoCotacoes ?? []), ...v.cotacoes.flatMap((c) => c.ptax?.historico ?? [])]),
    ...(v.locacao?.regraComissao?.historico ?? []),
  ].sort((a, b) => b.data.localeCompare(a.data));
  return (
    <section className="cartao" data-teste="painel-historico">
      <h2>Histórico da Proposta {p.numero}</h2>
      <ul className="linha-tempo">
        {eventos.map((h, i) => (
          <li key={i}>
            <strong>{dataBr(h.data.slice(0, 10))}</strong>
            {h.data.length > 10 ? ` ${h.data.slice(11, 16)}` : ''} · {h.texto} <span className="texto-secundario">({h.autor})</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Resultado registrado por linha (somente leitura) nas versões emitidas. */
function ResultadoLeitura({ v }: { v: Versao }) {
  const { d } = useProposta();
  if (v.situacao === 'rascunho') return null;
  const parceiro = parceiroSelecionado(v);
  const venda = tipoDaVersao(v) === 'venda';
  return (
    <section className="secao-editor" data-teste="resultado-leitura">
      <h2>Resultado por linha</h2>
      <div className="rolagem">
        <table className="tabela">
          <thead>
            <tr>
              <th>Linha</th>
              <th>Proposta</th>
              <th>Ganha</th>
              <th>Perdida</th>
              <th>Pendente</th>
              <th>Resultado</th>
              <th>Motivo da perda</th>
              <th>Fornecedor/parceiro</th>
            </tr>
          </thead>
          <tbody>
            {linhasResultadoDaVersao(v).map((l) => {
              const item = venda ? (l as unknown as ItemProposta) : undefined;
              const nome = item ? produtoDoItem(d, item).nome : (l as unknown as { descricao: string }).descricao;
              const forn = item ? item.entidadeId : parceiro;
              return (
                <tr key={l.id}>
                  <td>{nome}</td>
                  <td>{l.quantidade}</td>
                  <td>{l.quantidadeAceita}</td>
                  <td>{l.quantidadePerdida ?? 0}</td>
                  <td>{l.quantidade - l.quantidadeAceita - (l.quantidadePerdida ?? 0)}</td>
                  <td>{RESULTADO_LINHA[l.resultado]}</td>
                  <td>
                    {l.motivoPerda || '—'}
                    {l.observacaoResultado ? ` — ${l.observacaoResultado}` : ''}
                  </td>
                  <td>{forn ? (entidadeDe(d, forn)?.fantasia ?? '—') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
